import React, {
  useRef,
  useLayoutEffect,
  useState,
  useEffect,
  forwardRef,
} from "react";
import { Grid } from "@mui/material";

const ScrollContainer = forwardRef(
  (
    {
      children,
      itemSize = { xs: 12, sm: 6, md: 4 },
      gap = "20px",
      speed = 2,
    },
    ref
  ) => {
    const scrollRef = useRef(null);
    const firstChildRef = useRef(null);

    const [childWidth, setChildWidth] = useState(300);
    const extendedChildren = [...children, ...children];

    // Measure first item width
    useLayoutEffect(() => {
      if (firstChildRef.current) {
        const width = firstChildRef.current.getBoundingClientRect().width;
        setChildWidth(width);
      }
    }, [children]);

    // AUTO SCROLL
    useEffect(() => {
      const slider = scrollRef.current;
      if (!slider) return;

      let animationFrameId;

      const scrollLoop = () => {
        slider.scrollLeft += speed;

        if (slider.scrollLeft >= slider.scrollWidth / 2) {
          slider.scrollLeft = 0;
        }

        animationFrameId = requestAnimationFrame(scrollLoop);
      };

      animationFrameId = requestAnimationFrame(scrollLoop);

      return () => cancelAnimationFrame(animationFrameId);
    }, [speed]);

    // DRAG TO SCROLL
    useEffect(() => {
      const slider = scrollRef.current;
      if (!slider) return;

      let isDown = false;
      let startX;
      let scrollLeft;

      const mouseDown = (e) => {
        isDown = true;
        slider.style.cursor = "grabbing";
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      };

      const mouseLeave = () => {
        isDown = false;
        slider.style.cursor = "grab";
      };

      const mouseUp = () => {
        isDown = false;
        slider.style.cursor = "grab";
      };

      const mouseMove = (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = x - startX;
        slider.scrollLeft = scrollLeft - walk;
      };

      slider.addEventListener("mousedown", mouseDown);
      slider.addEventListener("mouseleave", mouseLeave);
      slider.addEventListener("mouseup", mouseUp);
      slider.addEventListener("mousemove", mouseMove);

      slider.style.cursor = "grab";

      return () => {
        slider.removeEventListener("mousedown", mouseDown);
        slider.removeEventListener("mouseleave", mouseLeave);
        slider.removeEventListener("mouseup", mouseUp);
        slider.removeEventListener("mousemove", mouseMove);
      };
    }, []);

    return (
      <div
        ref={scrollRef}
        style={{
          display: "flex",
          overflowX: "auto", // 👈 TOUCHPAD SCROLL ENABLED
          flexWrap: "nowrap",
          gap,
          scrollbarWidth: "none",
        }}
      >
        {extendedChildren.map((child, index) => (
          <Grid
            key={index}
            ref={index === 0 ? firstChildRef : null}
            sx={{ flex: "0 0 auto" }}
            size={itemSize}
          >
            {child}
          </Grid>
        ))}
      </div>
    );
  }
);

export default ScrollContainer;
